import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import logger from "@logger";
import {ETFPriceHistory} from "@/integrations/supabase/etf.models.ts";
import {getIsoDateStringFromTimeRange} from "@timeUtils";

/**
 * ETF Price service manages ETF price operations
 * @class ETFPriceService
 * @example
 * const service = new ETFPriceService();
 * const price = await service.fetchETFPrice({ symbol: 'SPY', exchange: 'NYSE' });
 */
class ETFPriceService extends BaseService {

    /**
     * Fetch ETF price for a given ETF
     * @param {ETFPriceFetchRequest} request request to fetch ETF price
     * @returns {Promise<ETFPriceResponse>}
     */
    async fetchETFPrice(request: ETFPriceFetchRequest): Promise<ETFPriceResponse> {
        const { symbol, exchange } = request;
        logger.info(`Fetching ETF price for symbol ${symbol} and exchange: ${exchange}`, {symbol, exchange})
        const { data, error } = await supabase.functions.invoke('etf-price', {
            body: { symbol, exchange },
        });

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data
    }

    /**
     * Fetch ETF price history for a given ETF
     * @param request
     */
    async fetchETFPriceHistory(request: ETFPriceHistoryFetchRequest): Promise<ETFPriceHistory[]> {
        const { tickerSymbol, timePeriod = 'ALL' } = request;
        logger.info(`Fetching ETF price history symbol ${tickerSymbol}`, {tickerSymbol, timePeriod})
        let query = supabase
            .from('etf_price_history')
            .select('*')
            .eq('ticker_symbol', tickerSymbol)
            .order('fetched_at', { ascending: false });

        const startDate = getIsoDateStringFromTimeRange(timePeriod);
        if (startDate) {
            query = query.gte('fetched_at', startDate);
        }

        const { data, error } = await query;
        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Fetches the price history for multiple ETF tickers within a specified time period.
     *
     * @param {ETFMultiTickerPriceHistoryFetchRequest} request - The request containing the tickers and time period to query.
     *                                                           The `tickers` field is an array of ticker symbols, and
     *                                                           `timePeriod` specifies the desired time range for price history.
     * @return {Promise<ETFPriceHistory[]>} A promise that resolves to an array of `ETFPriceHistory` objects representing the historical price data for the specified tickers.
     *                                      Returns an empty array if no tickers are provided.
     * @throws Will throw an error if the query to the database fails.
     */
    async fetchMultiTickerPriceHistory(request: ETFMultiTickerPriceHistoryFetchRequest): Promise<ETFPriceHistory[]> {
        const { tickers, timePeriod } = request;
        logger.info(`Fetching ETF multi ticket price history for tickers ${tickers}`, {tickers, timePeriod})
        if (tickers.length === 0) return [];

        let query = supabase
            .from('etf_price_history')
            .select('*')
            .in('ticker_symbol', tickers)
            .order('fetched_at', { ascending: true });

        const startDate = getIsoDateStringFromTimeRange(timePeriod);
        if (startDate) query = query.gte('fetched_at', startDate);

        const { data, error } = await query;

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }
}

export default new ETFPriceService();
